import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import {
  GetClassesBySchoolId,
  GetStudentsWithLicense,
  GetUserByEmail,
} from "../../actions/adminQueries";
import CreateStudent from "../Teacher/CreateStudent";
import StudentTable from "../../components/Tables/StudentTable";
import { FormattedMessage, useIntl } from "react-intl";
import EditStudent from "../../components/Modal/EditStudent";
import StudentLicenseManagemenModal from "../../components/Modal/StudentLicenseManagemenModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation } from "react-router-dom";
import useCartStore from "../../stores/useCartStore";
import { attachClassToStudent } from "../../Utilities/studentHelper";
import {
  buildAdminOrderContext,
  canBuyForStudent,
  findLicenseStatus,
  findProgramLicenseIdForCostCenter,
  programLicensesFor,
} from "../../Utilities/licenseHelper";
import useDeleteStudent from "../../hooks/useDeleteStudent";

function AdminStudents() {
  const intl = useIntl();
  const location = useLocation();
  const [filterSearch, setFilterSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [studentEditModal, setStudentEditModal] = useState(false);
  const [school] = useState(
    location.state
      ? location.state.school
      : JSON.parse(localStorage.getItem("school")),
  );
  const [displayCreateStudentModal, setDisplayCreateStudentModal] =
    useState(false);
  const [studentToUpdate, setStudentToUpdate] = useState(null);
  const [studentToBuyLicenseFor, setStudentToBuyLicenseFor] = useState(null);

  const classesQuery = GetClassesBySchoolId(school.id);
  const licenseStatusQuery = GetStudentsWithLicense(school.id);
  const currentUserQuery = GetUserByEmail(localStorage.getItem("email"));
  const clearCart = useCartStore((s) => s.clearCart);

  // Kundvagnen är global och överlever att modalen stängs. Rensas den inte följer
  // föregående elevs rader med in i nästa order.
  const openLicenseModal = (student) => {
    clearCart();
    setStudentToBuyLicenseFor(student);
  };

  const closeLicenseModal = () => {
    clearCart();
    setStudentToBuyLicenseFor(null);
  };

  const orderContext = useMemo(
    () =>
      buildAdminOrderContext(
        school,
        currentUserQuery.data,
        studentToBuyLicenseFor?.class?.programme?.costCenter,
        "/superuser/schools/students",
      ),
    [school, currentUserQuery.data, studentToBuyLicenseFor],
  );

  // Ny array varje render skulle nollställa admins egna kryss i modalen, eftersom
  // ShopStudentsSelect har preselectedStudents som beroende och skriver om urvalet.
  const preselectedStudents = useMemo(
    () => (studentToBuyLicenseFor ? [studentToBuyLicenseFor] : []),
    [studentToBuyLicenseFor],
  );

  const licensesFor = (student) =>
    programLicensesFor(
      school,
      findLicenseStatus(licenseStatusQuery.data, student.id),
      findProgramLicenseIdForCostCenter(
        school,
        student.class?.programme?.costCenter,
      ),
    );

  // Medan licensstatusen hämtas saknas underlag: utan vakten läses varje elev som
  // "saknar licens" och knappen blir felaktigt aktiv innan svaret kommit.
  const canBuyLicenseFor = (student) =>
    !licenseStatusQuery.isPending &&
    canBuyForStudent(
      findLicenseStatus(licenseStatusQuery.data, student.id),
      findProgramLicenseIdForCostCenter(
        school,
        student.class?.programme?.costCenter,
      ),
    );

  useEffect(() => {
    localStorage.setItem("school", JSON.stringify(school));
  }, [school]);

  useEffect(() => {
    if (!classesQuery.isPending && classesQuery.data) {
      var tempStudents = [];
      classesQuery.data.forEach((c) => {
        c.students.forEach((s) => {
          tempStudents.push(attachClassToStudent(s, c));
        });
      });
      setStudents(tempStudents);
    }
  }, [classesQuery.data, classesQuery.isPending]);

  const toggleStudentEditModal = (student) => {
    setStudentToUpdate(null);

    if (student && student.hasOwnProperty("email")) {
      setStudentToUpdate(student);
    }
    setStudentEditModal(!studentEditModal);
  };

  const updateStudents = () => {
    classesQuery.refetch();
  };

  const { promptDeleteStudent, deleteStudentPrompt } =
    useDeleteStudent(updateStudents);

  const handleFilterSearch = (event) => {
    setFilterSearch(event.target.value);
  };

  const toggleCreateStudentModal = () => {
    setDisplayCreateStudentModal(!displayCreateStudentModal);
  };

  const updateClassesAndStudents = () => {
    setDisplayCreateStudentModal(false);
    updateStudents();
  };

  return (
    <div>
      <div className="card card-body">
        <h5 className="no-margin">Elever {school.name}</h5>
      </div>
      <div className="container-fluid admin-container">
        <Col>
          <Row className="search-row">
            <Col className="col-3">
              <Input
                placeholder={intl.formatMessage({ id: "SearchStudent" })}
                onChange={(data) => handleFilterSearch(data)}
              />
            </Col>
            <Col className="col-9">
              <Button
                color="primary"
                outline
                className="float-end"
                onClick={() => toggleCreateStudentModal()}
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </Col>
          </Row>
          <StudentTable
            filter={filterSearch}
            toggleEdit={toggleStudentEditModal}
            onDeleteStudent={promptDeleteStudent}
            onBuyLicense={openLicenseModal}
            canBuyLicenseFor={canBuyLicenseFor}
            licensesFor={licensesFor}
            showClass={true}
            students={students}
          />
        </Col>
      </div>

      <Modal
        size="lg"
        isOpen={displayCreateStudentModal}
        toggle={toggleCreateStudentModal}
      >
        <ModalHeader toggle={toggleCreateStudentModal}>
          <FormattedMessage id="Views.Teacher.Students.Modal.AddNewStudent" />
        </ModalHeader>
        <ModalBody>
          <CreateStudent
            classes={classesQuery.data || []}
            updateStudents={updateClassesAndStudents}
            dismissModal={toggleCreateStudentModal}
          />
        </ModalBody>
      </Modal>

      <EditStudent
        toggle={toggleStudentEditModal}
        isOpen={studentEditModal}
        student={studentToUpdate}
        updateStudents={updateStudents}
        classes={classesQuery.data || []}
      />

      {deleteStudentPrompt}

      <StudentLicenseManagemenModal
        isOpen={studentToBuyLicenseFor !== null}
        toggle={closeLicenseModal}
        preselectedStudents={preselectedStudents}
        className={studentToBuyLicenseFor?.class.name}
        students={students}
        orderContext={orderContext}
      />
    </div>
  );
}

export default AdminStudents;
